import React, { useState, useEffect } from 'react';
import api, { toArr } from '../api';

const C = {
  primary: '#6366f1', secondary: '#10b981', accent: '#f59e0b', danger: '#ef4444',
  card: 'rgba(255,255,255,0.05)', cardBorder: 'rgba(99,102,241,0.15)',
  text: '#fff', textMuted: 'rgba(255,255,255,0.5)',
};

const iS = {
  width: '100%', background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10,
  padding: '10px 14px', color: '#fff', fontSize: '0.875rem',
  outline: 'none', boxSizing: 'border-box',
};

const Badge = ({ label, color }) => (
  <span style={{ fontSize: '0.65rem', fontWeight: 700, color,
    background: `${color}18`, border: `1px solid ${color}30`,
    padding: '2px 8px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: 0.5 }}>
    {label}
  </span>
);

// ── Mock Questions Tab ────────────────────────────────────────────────────────

const QuestionsTab = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [newQ, setNewQ] = useState({
    category: 'GenAI', question: '', options: ['', '', '', ''], answer: '', explanation: '',
  });

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const fetchQuestions = () => {
    setLoading(true);
    api.get('/mockquestions')
      .then(res => setQuestions(toArr(res.data)))
      .catch(() => setQuestions([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchQuestions(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/mockquestions', { ...newQ, options: newQ.options.map(text => ({ text })) });
      showMsg('success', 'Question added successfully!');
      setNewQ({ category: 'GenAI', question: '', options: ['', '', '', ''], answer: '', explanation: '' });
      setShowForm(false);
      fetchQuestions();
    } catch {
      showMsg('error', 'Failed to save. Check API connection.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await api.delete(`/mockquestions/${id}`);
      setQuestions(prev => prev.filter(q => q.id !== id));
      showMsg('success', 'Question deleted.');
    } catch {
      showMsg('error', 'Delete failed.');
    }
  };

  const catColor = { GenAI: C.primary, Cloud: C.secondary, ML: C.accent, DevOps: '#ec4899' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: '0.9rem', color: C.textMuted }}>{questions.length} questions in database</div>
        <button onClick={() => setShowForm(v => !v)} style={{
          background: showForm ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg, #6366f1, #10b981)',
          border: 'none', borderRadius: 10, padding: '10px 20px',
          color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem',
        }}>
          {showForm ? '✕ Cancel' : '+ Add Question'}
        </button>
      </div>

      {msg.text && (
        <div style={{ background: msg.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${msg.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
          borderRadius: 10, padding: '12px 16px', color: msg.type === 'success' ? C.secondary : C.danger,
          fontWeight: 600, fontSize: '0.875rem', marginBottom: 20 }}>
          {msg.type === 'success' ? '✅' : '⚠️'} {msg.text}
        </div>
      )}

      {showForm && (
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, marginBottom: 24 }}>
          <div style={{ fontWeight: 700, color: C.text, marginBottom: 20 }}>New Mock Question</div>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.72rem', color: C.textMuted,
                  textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Category</label>
                <select value={newQ.category} onChange={e => setNewQ(p => ({ ...p, category: e.target.value }))}
                  style={{ ...iS, background: 'rgba(30,27,60,0.9)' }}>
                  <option>GenAI</option><option>Cloud</option><option>ML</option><option>DevOps</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.72rem', color: C.textMuted,
                  textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Correct Answer</label>
                <input value={newQ.answer} onChange={e => setNewQ(p => ({ ...p, answer: e.target.value }))}
                  placeholder="Paste the correct option text" required style={iS} />
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', color: C.textMuted,
                textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Question</label>
              <textarea value={newQ.question} onChange={e => setNewQ(p => ({ ...p, question: e.target.value }))}
                placeholder="Enter the question text" required rows={3}
                style={{ ...iS, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', color: C.textMuted,
                textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Answer Options</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {newQ.options.map((opt, i) => (
                  <input key={i} value={opt}
                    onChange={e => { const o = [...newQ.options]; o[i] = e.target.value; setNewQ(p => ({ ...p, options: o })); }}
                    placeholder={`Option ${i + 1}`} required style={iS} />
                ))}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.72rem', color: C.textMuted,
                textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Explanation</label>
              <textarea value={newQ.explanation} onChange={e => setNewQ(p => ({ ...p, explanation: e.target.value }))}
                placeholder="Explain why this answer is correct" rows={2}
                style={{ ...iS, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
            <button type="submit" style={{ background: 'linear-gradient(135deg, #6366f1, #10b981)',
              border: 'none', borderRadius: 10, padding: 12, color: '#fff',
              fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>Save Question</button>
          </form>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', color: C.textMuted, padding: 40 }}>Loading questions...</div>
      ) : questions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📝</div>
          <div style={{ color: C.textMuted }}>No questions yet. Add your first question above.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {questions.map((q, i) => (
            <div key={q.id || i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`,
              borderRadius: 12, padding: '18px 20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Badge label={q.category} color={catColor[q.category] || C.primary} />
                  <span style={{ fontSize: '0.72rem', color: C.textMuted }}>Q{i + 1}</span>
                </div>
                <button onClick={() => handleDelete(q.id)} style={{
                  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                  borderRadius: 6, padding: '4px 10px', color: C.danger,
                  fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                }}>Delete</button>
              </div>
              <div style={{ fontWeight: 600, color: C.text, fontSize: '0.9rem', marginBottom: 10 }}>{q.question}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                {(q.options || []).map((opt, j) => {
                  const val = opt.text || opt;
                  const correct = val === q.answer;
                  return (
                    <div key={j} style={{ fontSize: '0.8rem', padding: '5px 10px', borderRadius: 6,
                      color: correct ? C.secondary : C.textMuted,
                      background: correct ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${correct ? 'rgba(16,185,129,0.2)' : 'transparent'}` }}>
                      {correct ? '✓ ' : ''}{val}
                    </div>
                  );
                })}
              </div>
              {q.explanation && (
                <div style={{ fontSize: '0.78rem', color: C.textMuted, fontStyle: 'italic' }}>{q.explanation}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── User Management Tab ───────────────────────────────────────────────────────

const roleColors = { admin: '#ef4444', instructor: '#f59e0b', learner: '#10b981' };

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [msg, setMsg] = useState({ type: '', text: '' });

  const showMsg = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const fetchUsers = () => {
    setLoading(true);
    api.get('/users')
      .then(res => setUsers(toArr(res.data)))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/users/${userId}`, { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
      showMsg('success', 'User role updated.');
    } catch {
      showMsg('error', 'Failed to update role.');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Permanently delete this user? This cannot be undone.')) return;
    try {
      await api.delete(`/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      showMsg('success', 'User removed.');
    } catch {
      showMsg('error', 'Failed to delete user.');
    }
  };

  const filtered = users.filter(u => {
    const matchSearch = (u.name || u.email || '').toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole === 'all' || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const counts = users.reduce((acc, u) => { acc[u.role] = (acc[u.role] || 0) + 1; return acc; }, {});

  return (
    <div>
      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total Users', value: users.length, color: C.primary },
          { label: 'Learners',    value: counts.learner || 0, color: C.secondary },
          { label: 'Instructors', value: counts.instructor || 0, color: C.accent },
          { label: 'Admins',      value: counts.admin || 0, color: C.danger },
        ].map((s, i) => (
          <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`,
            borderRadius: 12, padding: '16px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: s.color }}>{loading ? '—' : s.value}</div>
            <div style={{ fontSize: '0.72rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {msg.text && (
        <div style={{ background: msg.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${msg.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
          borderRadius: 10, padding: '12px 16px', color: msg.type === 'success' ? C.secondary : C.danger,
          fontWeight: 600, fontSize: '0.875rem', marginBottom: 16 }}>
          {msg.type === 'success' ? '✅' : '⚠️'} {msg.text}
        </div>
      )}

      {/* Search & Filter */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍  Search by name or email..."
          style={{ flex: 1, minWidth: 200, ...iS }} />
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
          style={{ ...iS, width: 'auto', background: 'rgba(30,27,60,0.9)' }}>
          <option value="all">All Roles</option>
          <option value="learner">Learners</option>
          <option value="instructor">Instructors</option>
          <option value="admin">Admins</option>
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: C.textMuted, padding: 40 }}>Loading users...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
          <div style={{ color: C.textMuted }}>
            {users.length === 0 ? 'No users found. Configure VITE_API_URL to load real user data.' : 'No users match your filter.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((u, i) => (
            <div key={u.id || i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`,
              borderRadius: 12, padding: '16px 20px',
              display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Avatar */}
              <div style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                background: `linear-gradient(135deg, ${roleColors[u.role] || C.primary}, #10b981)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 700, color: '#fff' }}>
                {(u.name || u.email || 'U').charAt(0).toUpperCase()}
              </div>
              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#fff', fontSize: '0.9rem',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {u.name || '—'}
                </div>
                <div style={{ fontSize: '0.75rem', color: C.textMuted, marginTop: 2,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {u.email}
                </div>
              </div>
              {/* Joined */}
              {u.createdAt && (
                <div style={{ fontSize: '0.72rem', color: C.textMuted, flexShrink: 0 }}>
                  Joined {new Date(u.createdAt).toLocaleDateString()}
                </div>
              )}
              {/* Role selector */}
              <select value={u.role || 'learner'}
                onChange={e => handleRoleChange(u.id, e.target.value)}
                style={{ ...iS, width: 'auto', background: 'rgba(30,27,60,0.95)',
                  padding: '6px 10px', fontSize: '0.8rem', flexShrink: 0,
                  color: roleColors[u.role] || C.textMuted }}>
                <option value="learner">Learner</option>
                <option value="instructor">Instructor</option>
                <option value="admin">Admin</option>
              </select>
              {/* Delete */}
              <button onClick={() => handleDelete(u.id)} style={{
                background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                borderRadius: 8, padding: '6px 12px', color: C.danger,
                fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
              }}>Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Reports Tab ───────────────────────────────────────────────────────────────

const ReportsTab = () => {
  const [data, setData] = useState({ users: 0, questions: 0, learners: 0, instructors: 0, admins: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([api.get('/users'), api.get('/mockquestions')])
      .then(([usersRes, questionsRes]) => {
        const users = usersRes.status === 'fulfilled' ? toArr(usersRes.value.data) : [];
        const questions = questionsRes.status === 'fulfilled' ? toArr(questionsRes.value.data) : [];
        const counts = users.reduce((a, u) => { a[u.role] = (a[u.role] || 0) + 1; return a; }, {});
        setData({
          users: users.length,
          questions: questions.length,
          learners: counts.learner || 0,
          instructors: counts.instructor || 0,
          admins: counts.admin || 0,
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const metrics = [
    { label: 'Total Users',      value: data.users,       icon: '👥', color: C.primary },
    { label: 'Learners',         value: data.learners,    icon: '🎓', color: C.secondary },
    { label: 'Instructors',      value: data.instructors, icon: '👨‍🏫', color: C.accent },
    { label: 'Question Bank',    value: data.questions,   icon: '📝', color: '#ec4899' },
    { label: 'Avg. Test Score',  value: '82%',            icon: '📊', color: '#06b6d4' },
    { label: 'Courses Available',value: '8',              icon: '📚', color: '#8b5cf6' },
  ];

  return (
    <div>
      <div style={{ fontSize: '0.9rem', color: C.textMuted, marginBottom: 24 }}>
        Platform-wide usage metrics and activity overview.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 28 }}>
        {metrics.map((m, i) => (
          <div key={i} style={{ background: C.card, border: `1px solid ${C.cardBorder}`,
            borderRadius: 14, padding: '20px', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: 80, height: 80,
              background: `radial-gradient(circle, ${m.color}20, transparent 70%)` }} />
            <div style={{ fontSize: 28, marginBottom: 10 }}>{m.icon}</div>
            <div style={{ fontSize: '1.8rem', fontWeight: 800, color: m.color }}>
              {loading ? '—' : m.value}
            </div>
            <div style={{ fontSize: '0.72rem', color: C.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 4 }}>
              {m.label}
            </div>
          </div>
        ))}
      </div>

      {/* Recent activity placeholder */}
      <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28 }}>
        <div style={{ fontWeight: 700, color: '#fff', marginBottom: 16, fontSize: '0.95rem' }}>Platform Health</div>
        {[
          { label: 'User Registration Rate',  value: 94, color: C.primary },
          { label: 'Course Completion Rate',  value: 78, color: C.secondary },
          { label: 'Test Pass Rate',          value: 65, color: C.accent },
          { label: 'Daily Active Users',      value: 82, color: '#ec4899' },
        ].map((s, i) => (
          <div key={i} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.7)' }}>{s.label}</span>
              <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#fff' }}>{s.value}%</span>
            </div>
            <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 100, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${s.value}%`,
                background: `linear-gradient(90deg, ${s.color}, ${C.secondary})`,
                borderRadius: 100, boxShadow: `0 0 8px ${s.color}60` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Settings Tab ──────────────────────────────────────────────────────────────

const SettingsTab = () => {
  const [settings, setSettings] = useState({
    platformName: 'Eliture LMS',
    defaultLanguage: 'English',
    sessionDuration: '60',
    maxAttempts: '3',
    passPercentage: '60',
    supportEmail: 'support@eliture.com',
  });
  const [saved, setSaved] = useState(false);

  const handleSave = (e) => {
    e.preventDefault();
    // Persist to localStorage for now; wire to API when settings endpoint is added
    localStorage.setItem('eliture_settings', JSON.stringify(settings));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div>
      {saved && (
        <div style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 10, padding: '12px 16px', color: C.secondary,
          fontWeight: 600, fontSize: '0.875rem', marginBottom: 20 }}>
          ✅ Settings saved successfully!
        </div>
      )}

      <form onSubmit={handleSave}>
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, color: '#fff', marginBottom: 20 }}>Platform Configuration</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {[
              { label: 'Platform Name',    key: 'platformName',     placeholder: 'Eliture LMS' },
              { label: 'Support Email',    key: 'supportEmail',     placeholder: 'support@example.com' },
              { label: 'Default Language', key: 'defaultLanguage',  placeholder: 'English', isSelect: ['English', 'Hindi', 'Tamil', 'Telugu', 'Kannada'] },
              { label: 'Session Duration (min)', key: 'sessionDuration', placeholder: '60', type: 'number' },
            ].map(f => (
              <div key={f.key}>
                <label style={{ display: 'block', fontSize: '0.72rem', color: C.textMuted,
                  textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{f.label}</label>
                {f.isSelect ? (
                  <select value={settings[f.key]} onChange={e => setSettings(p => ({ ...p, [f.key]: e.target.value }))}
                    style={{ ...iS, background: 'rgba(30,27,60,0.9)' }}>
                    {f.isSelect.map(o => <option key={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={f.type || 'text'} value={settings[f.key]}
                    onChange={e => setSettings(p => ({ ...p, [f.key]: e.target.value }))}
                    placeholder={f.placeholder} style={iS} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, color: '#fff', marginBottom: 20 }}>Assessment Rules</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {[
              { label: 'Max Test Attempts',   key: 'maxAttempts',     placeholder: '3', type: 'number' },
              { label: 'Pass Percentage (%)', key: 'passPercentage',  placeholder: '60', type: 'number' },
            ].map(f => (
              <div key={f.key}>
                <label style={{ display: 'block', fontSize: '0.72rem', color: C.textMuted,
                  textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{f.label}</label>
                <input type={f.type} value={settings[f.key]}
                  onChange={e => setSettings(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.placeholder} min="0" style={iS} />
              </div>
            ))}
          </div>
        </div>

        {/* Read-only info */}
        <div style={{ background: C.card, border: `1px solid ${C.cardBorder}`, borderRadius: 16, padding: 28, marginBottom: 24 }}>
          <div style={{ fontWeight: 700, color: '#fff', marginBottom: 16 }}>System Info</div>
          {[
            { label: 'API Endpoint',   value: import.meta.env.VITE_API_URL || '/lms' },
            { label: 'Environment',    value: import.meta.env.MODE || 'development' },
            { label: 'Build Version',  value: '1.0.0' },
          ].map((s, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ fontSize: '0.875rem', color: C.textMuted }}>{s.label}</span>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#fff', fontFamily: 'monospace' }}>{s.value}</span>
            </div>
          ))}
        </div>

        <button type="submit" style={{ background: 'linear-gradient(135deg, #6366f1, #10b981)',
          border: 'none', borderRadius: 10, padding: '12px 28px', color: '#fff',
          fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem',
          boxShadow: '0 4px 15px rgba(99,102,241,0.3)' }}>
          Save Settings
        </button>
      </form>
    </div>
  );
};

// ── Admin Panel Shell ─────────────────────────────────────────────────────────

const ElitureAdminPanel = () => {
  const [activeTab, setActiveTab] = useState('questions');

  const tabs = [
    { key: 'questions', label: 'Mock Questions', icon: '📝' },
    { key: 'users',     label: 'User Management', icon: '👥' },
    { key: 'reports',   label: 'Reports',         icon: '📊' },
    { key: 'settings',  label: 'LMS Settings',    icon: '⚙' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: '#fff' }}>Admin Panel</div>
        <div style={{ fontSize: '0.8rem', color: C.textMuted, marginTop: 4 }}>
          Manage questions, users, reports, and platform settings
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 28,
        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(99,102,241,0.1)',
        borderRadius: 12, padding: 4, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            flex: 1, minWidth: 120, padding: '10px 16px', borderRadius: 10, border: 'none',
            background: activeTab === t.key
              ? 'linear-gradient(135deg, rgba(99,102,241,0.3), rgba(16,185,129,0.2))'
              : 'transparent',
            color: activeTab === t.key ? '#fff' : C.textMuted,
            fontWeight: activeTab === t.key ? 700 : 400,
            cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.2s',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'questions' && <QuestionsTab />}
      {activeTab === 'users'     && <UsersTab />}
      {activeTab === 'reports'   && <ReportsTab />}
      {activeTab === 'settings'  && <SettingsTab />}
    </div>
  );
};

export default ElitureAdminPanel;
